import { dotDiamondPrints } from "encamp-shared/src/waste/dotBlockMarking";
import { DotMarkingContainer } from "encamp-shared/src/waste/dotMarkingGroup";
import { hasDotBlock, labelPrintsLegend } from "encamp-shared/src/waste/hazardousWasteLabelVisuals";
import { labelRcraPictograms } from "encamp-shared/src/waste/rcraPictograms";
import {
  LabelContentFieldsFragment,
  WasteAccumulationAreaType,
  WasteClassification,
  WasteKind,
} from "generated-graphql/graphql";
import { QR_CODE_SERVICE_URL } from "util/qrCodeService";

type DotProfileContent = Pick<
  LabelContentFieldsFragment,
  "dotProperShippingName" | "dotIdNumber" | "dotHazardClass"
>;

/**
 * Whether a container's DOT shipping profile is entirely unset, and so whether
 * the DOT block renders. The inverse of the shared `hasDotBlock`, which the
 * printed label gates the same block on -- shared rather than reimplemented so
 * the preview cannot show a block the PDF omits.
 */
export function isDotProfileIncomplete(content: DotProfileContent): boolean {
  return !hasDotBlock(content);
}

/**
 * Whether the label is about to print with no indication of the hazards of its
 * contents.
 *
 * 40 CFR 262.15(a)(5)(ii), 262.16(b)(6)(i)(B) and 262.17(a)(5)(i)(B) require
 * one alongside the marking and the accumulation date. It is judged on what
 * prints: a DOT hazard diamond (the stream has a DOT profile, the packaging
 * takes the hazard label, and the class has a symbol) or at least one RCRA
 * pictogram. A DOT profile whose class has no symbol prints neither.
 *
 * Scoped to labels carrying the hazardous-waste marking, since that is where
 * the requirement applies: used oil (279.22(c)(1)) and universal waste
 * (273.14) need only their own marking.
 */
export function isHazardIndicationMissing(
  content: Pick<
    LabelContentFieldsFragment,
    | "wasteKind"
    | "classification"
    | "universalWasteCategory"
    | "dotProperShippingName"
    | "dotIdNumber"
    | "dotHazardClass"
    | "dotSubsidiaryHazardClass"
    | "federalWasteCodes"
    | "profileIsIgnitable"
    | "profileIsCorrosive"
    | "profileIsReactive"
    | "profileIsToxic"
  >,
  container?: DotMarkingContainer
): boolean {
  if (!labelPrintsLegend(content)) return false;
  return (
    !dotDiamondPrints(content, container) &&
    labelRcraPictograms({ ...content, ...container }).length === 0
  );
}

type LegendFieldsContent = Pick<
  LabelContentFieldsFragment,
  "wasteKind" | "classification" | "universalWasteCategory"
>;

/**
 * Whether the label prints the 262.32(b)(3) "EPA ID Number" line with no
 * number. A very small quantity generator may have no EPA ID, so the warning
 * asks for it only if the generator has one.
 */
export function isEpaIdMissing(
  content: LegendFieldsContent & Pick<LabelContentFieldsFragment, "epaId">
): boolean {
  return labelPrintsLegend(content) && !content.epaId;
}

/** Whether the label prints the 262.32(b)(5) "EPA Waste Codes" line empty. */
export function areWasteCodesMissing(
  content: LegendFieldsContent & Pick<LabelContentFieldsFragment, "federalWasteCodes">
): boolean {
  return labelPrintsLegend(content) && content.federalWasteCodes.length === 0;
}

/**
 * Whether a used oil stream carries EPA hazardous waste codes. Used oil
 * managed under 40 CFR 279 is marked "Used Oil" (279.22(c)), and a code such
 * as D001 printed under it reads as hazardous waste.
 */
export function isUsedOilWithWasteCodes(
  content: Pick<LabelContentFieldsFragment, "wasteKind" | "federalWasteCodes">
): boolean {
  return content.wasteKind === WasteKind.UsedOil && content.federalWasteCodes.length > 0;
}

/**
 * Whether a pharmaceutical stream that is not RCRA hazardous is marked as a
 * hazardous waste pharmaceutical. 40 CFR 266.502(e) marks hazardous waste
 * pharmaceuticals; 266.502(d)(4) lets other pharmaceuticals be mixed in, so
 * the header stays and the user is told. Non-hazardous and state-regulated
 * streams both get the warning.
 */
export function isNonhazardousPharmaceutical(
  content: Pick<LabelContentFieldsFragment, "wasteKind" | "classification">
): boolean {
  return (
    content.wasteKind === WasteKind.Pharmaceutical &&
    (content.classification === WasteClassification.Nonhazardous ||
      content.classification === WasteClassification.StateRegulated)
  );
}

/**
 * Whether a central-accumulation-area label is about to print with no
 * accumulation start date, which 40 CFR 262.16(b)(6)(i)(C) and
 * 262.17(a)(5)(i)(C) require to be visible on the container.
 *
 * A satellite area is exempt -- it is volume-limited rather than time-limited,
 * and only needs a date once the excess starts accumulating
 * (262.15(a)(6)(iii)) -- so an undated satellite label is correct and must not
 * warn.
 */
export function isAccumulationStartDateMissing(
  content: Pick<LabelContentFieldsFragment, "accumulationStartDate" | "accumulationAreaType">
): boolean {
  return (
    content.accumulationAreaType === WasteAccumulationAreaType.Central &&
    content.accumulationStartDate == null
  );
}

/**
 * Whether a universal-waste container is about to print a marking that does
 * not name its waste type.
 *
 * 40 CFR 273.14 requires the marking to identify the TYPE of universal waste
 * ("Universal Waste - Lamp(s)"); a bare "Universal Waste" is not one of the
 * authorized phrases, so a label without the type goes on the drum
 * non-compliant. The category is derived onto the stream from its waste lines,
 * so it is absent while a container accumulates ahead of its first shipment,
 * and absent when the stream's lines disagree about which universal waste it
 * holds.
 */
export function isUniversalWasteTypeMissing(
  content: Pick<LabelContentFieldsFragment, "wasteKind" | "universalWasteCategory">
): boolean {
  return content.wasteKind === WasteKind.UniversalWaste && content.universalWasteCategory == null;
}

/**
 * Whether the marking is about to be chosen with nothing to choose from.
 *
 * A stream always carries a classification, so this is the container with no
 * stream assigned (`WasteContainer.wasteStreamId` is nullable).
 * `labelHeaderFor` prints the conservative "HAZARDOUS WASTE" there rather than
 * asserting "NON-HAZARDOUS WASTE" from missing data -- safe to handle, but not
 * a fact about this drum, so say so instead of letting it read as one.
 *
 * Only the kinds that leave the marking to classification count: used oil,
 * universal waste and pharmaceuticals name themselves whatever the
 * classification is.
 */
export function isClassificationMissing(
  content: Pick<LabelContentFieldsFragment, "wasteKind" | "classification">
): boolean {
  const kindDecidesMarking = content.wasteKind != null && content.wasteKind !== WasteKind.Rcra;
  return content.classification == null && !kindDecidesMarking;
}

/**
 * Builds the qr-code-generator service URL for a container's label QR,
 * copying the param set QRCodeDisplay builds (components/QRCodeDisplay/index.tsx)
 * minus its screen/print theme colors, which don't apply to a printed label.
 */
export function qrImageUrl(qrUrl: string): string {
  const params = new URLSearchParams({
    text: qrUrl,
    width: "400",
    margin: "2",
    errorCorrectionLevel: "H",
  });
  return `${QR_CODE_SERVICE_URL}?${params.toString()}`;
}
